import React, { useEffect, useState } from "react";
import { Box, Typography, Fade, Zoom } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { keyframes } from "@mui/material/styles";

interface CompletionAnimationProps {
  show: boolean;
  onComplete?: () => void;
}

const confetti = keyframes`
  0% {
    transform: translateY(0) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateY(-100px) rotate(720deg);
    opacity: 0;
  }
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0) scale(1);
  }
  40% {
    transform: translateY(-30px) scale(1.1);
  }
  60% {
    transform: translateY(-15px) scale(1.05);
  }
`;

const CompletionAnimation: React.FC<CompletionAnimationProps> = ({
  show,
  onComplete,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (show) {
      setIsVisible(true);
      const timer = setTimeout(() => {
        setIsVisible(false);
        onComplete?.();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [show, onComplete]);

  if (!isVisible) return null;

  const confettiColors = [
    "#6a11cb",
    "#2575fc",
    "#ffd89b",
    "#19547b",
    "#764ba2",
  ];

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "none",
        zIndex: 9999,
      }}
    >
      {/* Confetti particles */}
      {[...Array(20)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            width: 10,
            height: 10,
            backgroundColor: confettiColors[i % confettiColors.length],
            borderRadius: "50%",
            left: `${50 + (Math.random() - 0.5) * 100}%`,
            top: "50%",
            animation: `${confetti} ${1 + Math.random()}s ease-out`,
            animationDelay: `${Math.random() * 0.3}s`,
          }}
        />
      ))}

      {/* Success message */}
      <Fade in={isVisible} timeout={500}>
        <Box
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            borderRadius: 3,
            p: 4,
            boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            backdropFilter: "blur(10px)",
            textAlign: "center",
          }}
        >
          <Zoom in={isVisible} timeout={700}>
            <CheckCircleIcon
              sx={{
                fontSize: 80,
                color: "#4caf50",
                mb: 2,
                animation: `${bounce} 1s ease-in-out`,
              }}
            />
          </Zoom>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Task Completed!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Great job! Your progress has been saved.
          </Typography>
        </Box>
      </Fade>
    </Box>
  );
};

export default CompletionAnimation;
